/**
 * IDF Version Transition Engine
 *
 * Core engine for transitioning IDF documents between EnergyPlus versions.
 */

import type { IDFDocument, IDFObject, IDFField, SourceLocation } from '../parser/types';
import type {
  VersionTransition,
  TransitionResult,
  TransitionChange,
  TransitionWarning,
  TransitionError,
  ObjectTransitionRule,
  MultiObjectResult,
} from './types';
import { formatVersion } from './types';

/**
 * Helper to safely copy an IDFField, ensuring value is always defined
 * If field is undefined, returns a field with empty value
 */
export function copyField(field: IDFField | undefined): IDFField {
  if (!field) {
    return { value: '' };
  }
  const copy: IDFField = { value: field.value };
  if (field.inlineComment !== undefined) {
    copy.inlineComment = field.inlineComment;
  }
  if (field.units !== undefined) {
    copy.units = field.units;
  }
  return copy;
}

/**
 * Helper to add a change with optional objectName
 */
export function addChange(
  changes: TransitionChange[],
  type: TransitionChange['type'],
  objectClass: string,
  description: string,
  objectName?: string
): void {
  const change: TransitionChange = { type, objectClass, description };
  if (objectName !== undefined) {
    change.objectName = objectName;
  }
  changes.push(change);
}

/**
 * Helper to get object name for change tracking
 */
function getObjectName(object: IDFObject): string | undefined {
  return object.fields[0]?.value;
}

/**
 * Helper to create an IDFObject with proper optional handling
 */
export function createObject(
  id: string,
  className: string,
  fields: IDFField[],
  leadingComments: string[],
  sourceLocation?: SourceLocation
): IDFObject {
  const obj: IDFObject = {
    id,
    className,
    fields,
    leadingComments,
  };
  if (sourceLocation !== undefined) {
    obj.sourceLocation = sourceLocation;
  }
  return obj;
}

/**
 * Transition an IDF document from one version to another
 */
export function transitionDocument(
  document: IDFDocument,
  transition: VersionTransition
): TransitionResult {
  const changes: TransitionChange[] = [];
  const warnings: TransitionWarning[] = [];
  const errors: TransitionError[] = [];

  // Create a new document with the same structure
  const newDocument: IDFDocument = {
    version: formatVersion(transition.toVersion),
    objects: [],
    headerComments: [...document.headerComments],
    trailingComments: [...document.trailingComments],
  };

  // Process each object
  for (const object of document.objects) {
    const className = object.className.toUpperCase();

    // Check if object is deleted in new version
    if (transition.deletedObjects.has(className)) {
      addChange(
        changes,
        'object_deleted',
        object.className,
        `Deleted ${object.className} object (not supported in new version)`,
        getObjectName(object)
      );
      continue;
    }

    // Handle version object specially
    if (className === 'VERSION') {
      const newObject = transitionVersionObject(object, transition, changes);
      newDocument.objects.push(newObject);
      continue;
    }

    // Check for object-specific transition rules
    const rule = transition.objectRules.get(className);
    if (rule) {
      const result = applyObjectRule(object, rule, document, changes, warnings);
      if (result.primary) {
        newDocument.objects.push(result.primary);
      }
      // Add any additional objects created by the transformation
      for (const additionalObj of result.additional) {
        newDocument.objects.push(additionalObj);
      }
      // Collect changes and warnings from multi-object transformation
      changes.push(...result.changes);
      warnings.push(...result.warnings);
    } else {
      // Check for renamed objects
      const newClassName = transition.renamedObjects.get(className);
      if (newClassName) {
        const renamedObject = renameObject(object, newClassName);
        addChange(
          changes,
          'object_renamed',
          object.className,
          `Renamed ${object.className} to ${newClassName}`,
          getObjectName(object)
        );
        newDocument.objects.push(renamedObject);
      } else {
        // No transformation needed - copy as-is
        // But still check for report variable name changes
        const transformedObject = transformVariableReferences(
          object,
          transition.variableMappings,
          changes
        );
        newDocument.objects.push(transformedObject);
      }
    }
  }

  return {
    document: newDocument,
    success: errors.length === 0,
    warnings,
    errors,
    changes,
  };
}

/**
 * Transition the Version object
 */
function transitionVersionObject(
  object: IDFObject,
  transition: VersionTransition,
  changes: TransitionChange[]
): IDFObject {
  const oldVersion = object.fields[0]?.value ?? '';
  const newVersion = formatVersion(transition.toVersion);

  changes.push({
    type: 'version_update',
    objectClass: 'Version',
    description: `Updated version from ${oldVersion} to ${newVersion}`,
  });

  const firstField: IDFField = { value: newVersion };
  if (object.fields[0]?.inlineComment !== undefined) {
    firstField.inlineComment = object.fields[0].inlineComment;
  }

  return createObject(
    object.id,
    object.className,
    [firstField],
    [...object.leadingComments],
    object.sourceLocation
  );
}

/**
 * Apply an object transition rule
 */
function applyObjectRule(
  object: IDFObject,
  rule: ObjectTransitionRule,
  document: IDFDocument,
  _changes: TransitionChange[],
  _warnings: TransitionWarning[]
): MultiObjectResult {
  const result: MultiObjectResult = {
    primary: null,
    additional: [],
    changes: [],
    warnings: [],
  };

  const objectName = getObjectName(object);

  // Check if object should be deleted
  if (rule.deleteObject) {
    const change: TransitionChange = {
      type: 'object_deleted',
      objectClass: object.className,
      description: `Deleted ${object.className} object`,
    };
    if (objectName !== undefined) {
      change.objectName = objectName;
    }
    result.changes.push(change);
    return result;
  }

  // Use custom multi-object transform if provided
  if (rule.multiObjectTransform) {
    return rule.multiObjectTransform(object, document);
  }

  // Apply preprocessing if defined
  let workingObject = rule.preprocess ? rule.preprocess(object) : object;

  // Build new fields array based on mappings
  // Use a sparse array approach - only add fields that have mappings with valid source fields
  const fieldMap = new Map<number, IDFField>();

  // Apply each field mapping
  for (const mapping of rule.fieldMappings) {
    const sourceField = workingObject.fields[mapping.sourceIndex];

    switch (mapping.type) {
      case 'copy':
      case 'rename':
        // Only copy if source field exists and has a value, or if it's within the original field count
        if (sourceField && mapping.targetIndex >= 0) {
          fieldMap.set(mapping.targetIndex, copyField(sourceField));
        }
        break;

      case 'add_default':
        if (mapping.targetIndex >= 0) {
          fieldMap.set(mapping.targetIndex, {
            value: mapping.defaultValue ?? '',
          });
          const addChange: TransitionChange = {
            type: 'field_added',
            objectClass: object.className,
            description: `Added field at index ${mapping.targetIndex} with default value "${mapping.defaultValue ?? ''}"`,
          };
          if (objectName !== undefined) {
            addChange.objectName = objectName;
          }
          result.changes.push(addChange);
        }
        break;

      case 'transform':
        if (sourceField && mapping.transform && mapping.targetIndex >= 0) {
          const transformedField: IDFField = {
            value: mapping.transform(sourceField.value, workingObject),
          };
          if (sourceField.inlineComment !== undefined) {
            transformedField.inlineComment = sourceField.inlineComment;
          }
          if (sourceField.units !== undefined) {
            transformedField.units = sourceField.units;
          }
          fieldMap.set(mapping.targetIndex, transformedField);
        }
        break;

      case 'delete': {
        const deleteChange: TransitionChange = {
          type: 'field_deleted',
          objectClass: object.className,
          description: `Deleted field at index ${mapping.sourceIndex}`,
        };
        if (objectName !== undefined) {
          deleteChange.objectName = objectName;
        }
        result.changes.push(deleteChange);
        break;
      }
    }
  }

  // Convert sparse map to contiguous array
  const maxIndex = Math.max(...Array.from(fieldMap.keys()), -1);
  const newFields: IDFField[] = [];
  for (let i = 0; i <= maxIndex; i++) {
    const field = fieldMap.get(i);
    if (field) {
      newFields.push(field);
    }
  }

  // Track deleted fields
  const deletedMappings = rule.fieldMappings.filter((m) => m.type === 'delete');
  for (const mapping of deletedMappings) {
    const deletedField = workingObject.fields[mapping.sourceIndex];
    if (deletedField && deletedField.value.trim()) {
      const warning: TransitionWarning = {
        message: `Field value "${deletedField.value}" was deleted during transition`,
        objectClass: object.className,
        fieldIndex: mapping.sourceIndex,
      };
      if (objectName !== undefined) {
        warning.objectName = objectName;
      }
      result.warnings.push(warning);
    }
  }

  // Create the new object
  let newObject = createObject(
    object.id,
    rule.targetClass,
    newFields,
    [...object.leadingComments],
    object.sourceLocation
  );

  // Apply postprocessing if defined
  if (rule.postprocess) {
    newObject = rule.postprocess(newObject);
  }

  // Log the change if class name or field count changed
  if (
    rule.sourceClass.toUpperCase() !== rule.targetClass.toUpperCase() ||
    object.fields.length !== newFields.length
  ) {
    const fieldDiff = newFields.length - object.fields.length;
    const fieldChangeDesc =
      fieldDiff > 0
        ? `added ${fieldDiff} field(s)`
        : fieldDiff < 0
          ? `removed ${Math.abs(fieldDiff)} field(s)`
          : 'modified fields';

    const change: TransitionChange = {
      type: rule.sourceClass.toUpperCase() !== rule.targetClass.toUpperCase()
        ? 'object_renamed'
        : 'field_deleted',
      objectClass: object.className,
      description: `Transitioned ${object.className}: ${fieldChangeDesc}`,
    };
    if (objectName !== undefined) {
      change.objectName = objectName;
    }
    result.changes.push(change);
  }

  result.primary = newObject;
  return result;
}

/**
 * Rename an object's class name
 */
function renameObject(object: IDFObject, newClassName: string): IDFObject {
  return {
    ...object,
    className: newClassName,
  };
}

/**
 * Transform report variable references in Output:Variable and similar objects
 */
function transformVariableReferences(
  object: IDFObject,
  variableMappings: Map<string, string>,
  changes: TransitionChange[]
): IDFObject {
  const className = object.className.toUpperCase();
  const objectName = getObjectName(object);

  // Objects that contain report variable names
  const variableObjects = [
    'OUTPUT:VARIABLE',
    'OUTPUT:METER',
    'OUTPUT:METER:METERFILEONLY',
    'OUTPUT:METER:CUMULATIVE',
    'OUTPUT:METER:CUMULATIVE:METERFILEONLY',
    'ENERGYMANAGEMENTSYSTEM:SENSOR',
    'ENERGYMANAGEMENTSYSTEM:OUTPUTVARIABLE',
    'EXTERNALINTERFACE:VARIABLE',
    'OUTPUT:TABLE:TIMEBINS',
  ];

  if (!variableObjects.includes(className)) {
    return createObject(
      object.id,
      object.className,
      object.fields.map(copyField),
      [...object.leadingComments],
      object.sourceLocation
    );
  }

  // Field index containing the variable name
  // Output:Variable has it at index 1, others may vary
  const variableFieldIndex = className === 'OUTPUT:VARIABLE' ? 1 : 1;

  const newFields = object.fields.map((field, index) => {
    if (index !== variableFieldIndex) {
      return copyField(field);
    }

    const upperValue = field.value.toUpperCase();
    const newName = variableMappings.get(upperValue);

    if (newName) {
      if (newName === '<DELETE>') {
        const change: TransitionChange = {
          type: 'variable_renamed',
          objectClass: object.className,
          description: `Report variable "${field.value}" is deprecated and marked for deletion`,
        };
        if (objectName !== undefined) {
          change.objectName = objectName;
        }
        changes.push(change);
        // Keep the old name but add a warning comment
        const newField: IDFField = {
          value: field.value,
          inlineComment: field.inlineComment
            ? `${field.inlineComment} (DEPRECATED)`
            : '!- DEPRECATED variable',
        };
        if (field.units !== undefined) {
          newField.units = field.units;
        }
        return newField;
      } else {
        const change: TransitionChange = {
          type: 'variable_renamed',
          objectClass: object.className,
          description: `Renamed variable "${field.value}" to "${newName}"`,
        };
        if (objectName !== undefined) {
          change.objectName = objectName;
        }
        changes.push(change);
        const newField: IDFField = { value: newName };
        if (field.inlineComment !== undefined) {
          newField.inlineComment = field.inlineComment;
        }
        if (field.units !== undefined) {
          newField.units = field.units;
        }
        return newField;
      }
    }

    return copyField(field);
  });

  return createObject(
    object.id,
    object.className,
    newFields,
    [...object.leadingComments],
    object.sourceLocation
  );
}

/**
 * Chain multiple transitions together
 */
export function chainTransitions(
  document: IDFDocument,
  transitions: VersionTransition[]
): TransitionResult {
  let currentDocument = document;
  const allChanges: TransitionChange[] = [];
  const allWarnings: TransitionWarning[] = [];
  const allErrors: TransitionError[] = [];

  for (const transition of transitions) {
    const result = transitionDocument(currentDocument, transition);
    currentDocument = result.document;
    allChanges.push(...result.changes);
    allWarnings.push(...result.warnings);
    allErrors.push(...result.errors);

    if (!result.success) {
      return {
        document: currentDocument,
        success: false,
        warnings: allWarnings,
        errors: allErrors,
        changes: allChanges,
      };
    }
  }

  return {
    document: currentDocument,
    success: true,
    warnings: allWarnings,
    errors: allErrors,
    changes: allChanges,
  };
}

/**
 * Get the document version
 */
export function getDocumentVersion(document: IDFDocument): string {
  const versionObject = document.objects.find(
    (obj) => obj.className.toUpperCase() === 'VERSION'
  );
  return versionObject?.fields[0]?.value || document.version;
}
